import * as React from 'react';
import {
  MasonryEngineNodeAction,
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';

import {
  SegmentedControl,
  SegmentedControlRoot,
  type ControlList,
} from '@leagueplatform/genesis-core-alpha';
import { Box, Tabs } from '@leagueplatform/genesis-core';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

export const NODE_TYPE_SEGMENT_CONTROL = 'segmentedControl';

export interface MasonrySegmentControlNodeProperties {
  segmentedControlName: string;
  defaultSelectedControlId: string;
  controlList: ControlList[];
}

type MasonrySegmentControlNodeActions = {
  onClick?: MasonryEngineNodeAction;
};

type MasonrySegmentControlNodeSections = {
  items: MasonryEngineNode<string, any>[];
};

export type MasonrySegmentControlNode = MasonryEngineNode<
  typeof NODE_TYPE_SEGMENT_CONTROL,
  MasonrySegmentControlNodeProperties,
  MasonrySegmentControlNodeActions,
  MasonrySegmentControlNodeSections
>;

export type MasonrySegmentControlRendererProps =
  MasonryEngineNodeRendererProps<MasonrySegmentControlNode>;

export const findIndexById = (id: string, data: ControlList[]) =>
  data.findIndex((item: ControlList) => item.id === id);

export const MasonrySegmentControlRenderer = ({
  segmentedControlName,
  defaultSelectedControlId,
  controlList,
  items,
  onClick,
  rootElementRef,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonrySegmentControlRendererProps) => {
  const [viewSelection, setViewSelection] = React.useState<number>(0);
  const invokeAction = useInvokeAction();

  const clickHandler = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const value = event.currentTarget.dataset?.segment;
      if (!value) return;
      const index = findIndexById(value, controlList);
      setViewSelection(index);
      if (onClick) {
        const existingParams =
          onClick.payload?.extraQueryParams &&
          Array.isArray(onClick.payload.extraQueryParams)
            ? onClick.payload.extraQueryParams
            : [];

        invokeAction({
          event,
          ...onClick,
          payload: {
            ...onClick.payload,
            extraQueryParams: existingParams.concat([
              {
                key: 'segment',
                value: String(index),
              },
            ]),
          },
        });
      }
    },
    [controlList, invokeAction, onClick],
  );

  React.useEffect(() => {
    const selectedIndex = findIndexById(defaultSelectedControlId, controlList);
    if (selectedIndex !== -1) setViewSelection(selectedIndex);
  }, [controlList, defaultSelectedControlId]);

  if (!items || !items.length) return null;

  return (
    <Box
      css={{ marginBottom: '$oneAndHalf', ...css }}
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
    >
      <SegmentedControlRoot
        data-testid={locatorId}
        segmentedControlName={segmentedControlName}
        defaultSelectedControlId={defaultSelectedControlId}
      >
        <SegmentedControl.List aria-label={segmentedControlName}>
          {/* maps the buttons */}
          {controlList.map((item) => (
            <SegmentedControl.Button
              key={`${item.id}-panel`}
              value={item.id}
              aria-label={item.title}
              onClick={clickHandler}
            >
              {item.title}
            </SegmentedControl.Button>
          ))}
        </SegmentedControl.List>
        {/* maps the panels */}
        {controlList.map((item) => (
          <Tabs.TabPanel key={`${item.id}-list`} value={item.id}>
            {items[viewSelection]}
          </Tabs.TabPanel>
        ))}
      </SegmentedControlRoot>
    </Box>
  );
};
